import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

// --- Visual Components ---

// 1. Time Usage Visual (Donut Chart) - Refined Colors
const VisualTimeUsage = () => (
    <div className="w-full h-full flex flex-col md:flex-row items-center justify-center gap-8 p-6">
        {/* Donut Chart */}
        <div className="relative w-48 h-48 shrink-0">
            <div
                className="w-full h-full rounded-full"
                style={{
                    // Orange/Amber theme with low saturation grays
                    background: 'conic-gradient(#f59e0b 0% 35%, #fbbf24 35% 60%, #e5e7eb 60% 85%, #d1d5db 85% 100%)'
                }}
            ></div>
            <div className="absolute inset-0 m-auto w-32 h-32 bg-white rounded-full flex items-center justify-center flex-col shadow-inner">
                <span className="text-xs text-gray-400 font-bold">Total</span>
                <span className="text-2xl font-black text-gray-800">100%</span>
            </div>
        </div>

        {/* Legend */}
        <div className="flex flex-col gap-3 w-full max-w-[200px]">
            <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <span className="text-sm font-bold text-gray-700">Work</span>
                </div>
                <span className="text-sm font-bold text-gray-900">35%</span>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-300"></div>
                    <span className="text-sm font-bold text-gray-700">Study</span>
                </div>
                <span className="text-sm font-bold text-gray-900">25%</span>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                    <span className="text-sm font-bold text-gray-700">Sleep</span>
                </div>
                <span className="text-sm font-bold text-gray-900">25%</span>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                    <span className="text-sm font-bold text-gray-700">Other</span>
                </div>
                <span className="text-sm font-bold text-gray-900">15%</span>
            </div>
        </div>
    </div>
);

// 2. Activity Calendar Visual (Real Calendar View)
const VisualCalendar = () => {
    const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    // Mock data for a month (e.g., 30 days, starting on Tuesday)
    // 0 = empty, 1 = activity
    const calendarData = [
        0, 0, 1, 1, 0, 1, 0, // Week 1
        1, 1, 1, 0, 1, 1, 1, // Week 2
        0, 1, 1, 1, 1, 0, 0, // Week 3
        1, 1, 0, 1, 1, 1, 1, // Week 4
        0, 0, 0              // Week 5
    ];

    return (
        <div className="w-full max-w-xs bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <span className="font-bold text-gray-800">October 2023</span>
                <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                </div>
            </div>

            {/* Weekdays */}
            <div className="grid grid-cols-7 mb-2">
                {days.map((d, i) => (
                    <div key={i} className="text-center text-xs font-bold text-gray-400">
                        {d}
                    </div>
                ))}
            </div>

            {/* Days */}
            <div className="grid grid-cols-7 gap-1">
                {/* Empty slots for start of month */}
                <div className="aspect-square"></div>
                <div className="aspect-square"></div>

                {[...Array(30)].map((_, i) => {
                    const hasActivity = calendarData[i % calendarData.length] === 1;
                    return (
                        <div
                            key={i}
                            className={`aspect-square rounded-md flex items-center justify-center text-xs font-medium transition-all duration-300 ${hasActivity
                                ? 'bg-kotomilThemaColor text-white shadow-sm scale-105 font-bold'
                                : 'text-gray-400 hover:bg-gray-50'
                                }`}
                        >
                            {i + 1}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// 3. Trends Visual (Bar Chart)
const VisualTrends = () => (
    <div className="w-full h-full flex items-end justify-center gap-6 p-6 pb-8">
        {/* Last Month */}
        <div className="flex flex-col items-center gap-2 group">
            <div className="text-xs font-bold text-gray-400">Last Month</div>
            <div className="w-12 lg:w-16 h-24 bg-gray-200 rounded-t-xl relative overflow-hidden">
                <div className="absolute bottom-0 left-0 w-full h-full bg-gray-300/50"></div>
            </div>
            <div className="text-sm font-bold text-gray-500">45h</div>
        </div>

        {/* Arrow */}
        <div className="mb-8 text-gray-300">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
        </div>

        {/* This Month */}
        <div className="flex flex-col items-center gap-2 group">
            <div className="text-xs font-bold text-kotomilThemaColor">This Month</div>
            <div className="w-12 lg:w-16 h-40 bg-kotomilThemaColor rounded-t-xl relative overflow-hidden shadow-lg">
                <div className="absolute top-0 left-0 w-full h-full bg-white/10"></div>
                {/* Badge */}
                <div className="absolute top-2 right-2 w-2 h-2 bg-white rounded-full animate-pulse"></div>
            </div>
            <div className="text-xl font-black text-gray-800">72h</div>
        </div>
    </div>
);

function TopUiGallery(): ReactElement {
    const { t } = useTranslation('top');
    const gallerySubtitle = t('gallery.subtitle');
    const features = t('gallery.features', { returnObjects: true }) as Array<{
        title: string;
        description: string;
        experienceTitle?: string;
        experienceDescription?: string;
    }>;

    return (
        <section className="relative py-24 lg:py-32 bg-gray-50">
            {/* Decorative Wave Divider */}
            <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
                <svg className="relative block w-full h-12 lg:h-20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                    <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-white"></path>
                </svg>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-20 lg:mb-32">
                    <h2 className="text-3xl lg:text-5xl font-black text-gray-900 mb-6 tracking-tight">
                        {t('sections.gallery')}
                    </h2>
                    <p
                        className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: gallerySubtitle }}
                    />
                </div>

                {/* Vertical Stack Layout */}
                <div className="flex flex-col gap-16 lg:gap-24">

                    {/* Feature 1: Time Usage */}
                    <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-100 overflow-hidden flex flex-col lg:flex-row group hover:shadow-2xl transition-all duration-500">
                        <div className="flex-1 p-8 lg:p-16 flex flex-col justify-center">
                            <div className="inline-block bg-amber-100 text-amber-600 font-black text-xs px-4 py-1.5 rounded-full tracking-widest uppercase mb-8 self-start">
                                FEATURE 01
                            </div>
                            <h3
                                className="text-2xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight"
                                dangerouslySetInnerHTML={{ __html: features[0]?.title || '' }}
                            />
                            <p
                                className="text-gray-600 leading-loose text-lg"
                                dangerouslySetInnerHTML={{ __html: features[0]?.description || '' }}
                            />
                        </div>
                        <div className="flex-1 bg-gray-50 border-t lg:border-t-0 lg:border-l border-gray-100 min-h-[320px] lg:min-h-[400px] flex items-center justify-center relative overflow-hidden">
                            <VisualTimeUsage />
                        </div>
                    </div>

                    {/* Feature 2: Activity Calendar */}
                    <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-100 overflow-hidden flex flex-col lg:flex-row-reverse group hover:shadow-2xl transition-all duration-500">
                        <div className="flex-1 p-8 lg:p-16 flex flex-col justify-center">
                            <div className="inline-block bg-kotomilThemaColor/10 text-kotomilThemaColor font-black text-xs px-4 py-1.5 rounded-full tracking-widest uppercase mb-8 self-start">
                                FEATURE 02
                            </div>
                            <h3
                                className="text-2xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight"
                                dangerouslySetInnerHTML={{ __html: features[1]?.title || '' }}
                            />
                            <p
                                className="text-gray-600 leading-loose text-lg"
                                dangerouslySetInnerHTML={{ __html: features[1]?.description || '' }}
                            />
                        </div>
                        <div className="flex-1 bg-gray-50 border-t lg:border-t-0 lg:border-r border-gray-100 min-h-[320px] lg:min-h-[400px] flex items-center justify-center relative overflow-hidden">
                            <VisualCalendar />
                        </div>
                    </div>

                    {/* Feature 3: Trends */}
                    <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-100 overflow-hidden flex flex-col lg:flex-row group hover:shadow-2xl transition-all duration-500">
                        <div className="flex-1 p-8 lg:p-16 flex flex-col justify-center">
                            <div className="inline-block bg-blue-50 text-blue-600 font-black text-xs px-4 py-1.5 rounded-full tracking-widest uppercase mb-8 self-start">
                                FEATURE 03
                            </div>
                            <h3
                                className="text-2xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight"
                                dangerouslySetInnerHTML={{ __html: features[2]?.title || '' }}
                            />
                            <p
                                className="text-gray-600 leading-loose text-lg"
                                dangerouslySetInnerHTML={{ __html: features[2]?.description || '' }}
                            />
                        </div>
                        <div className="flex-1 bg-gray-50 border-t lg:border-t-0 lg:border-l border-gray-100 min-h-[320px] lg:min-h-[400px] flex items-center justify-center relative overflow-hidden">
                            <VisualTrends />
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}

export default TopUiGallery;
